import { useRef } from "preact/hooks";
import { useQuery } from "@tanstack/react-query";
import { getSassToken } from "../utils";
import { getIsEdge } from "../configs/env";
import { useAppSelector } from "../redux/hooks";
import { queryKeys } from "../lib/queryKeys";
import { userSessions } from "../lib/queryClient";

const SAS_TOKEN_REFRESH = 3 * 60 * 1000;

/**
 * The CDN token for the current portal.
 *
 * Minted once and shared by every component that asks: React Query dedupes
 * the request, hands the same value to every caller and refreshes it every
 * three minutes, so nothing needs its own timer, state copy or redux round
 * trip. Whichever route loads first, the router sees the same token and can
 * stamp it onto the portal branding.
 *
 * Returns "" until the token lands. A preview link carries its own token in
 * the URL (sasTokenPreview), and that one wins over a minted one.
 *
 * No portal, no token, unless the caller passes allowWorkspaceScope: only
 * kapp routes by id (/workspace/:id/kapp/...) and the korra-pdf-viewer embed
 * genuinely have no portal.
 *
 * sticky keeps the first token while the component stays on the same portal
 * and user, for assets loaded once (thumbnails, figures, branding): a new token
 * in their URL on every refresh re-downloads them and misses the cache. The
 * token still expires (15 min cloud, 30 min edge), so an asset first requested
 * later than that by the same mounted component gets refused.
 */
export const useSasToken = (
  workspaceId?: string,
  portalId?: string,
  options?: SasTokenOptions,
): string => useSasTokenState(workspaceId, portalId, options).token;

export interface SasTokenOptions {
  allowWorkspaceScope?: boolean;
  sticky?: boolean;
}

/**
 * useSasToken plus failed: true once the mint has errored (retries included)
 * and there is no token to fall back on. For callers that must show an error
 * instead of waiting on "" forever.
 */
export const useSasTokenState = (
  workspaceId?: string,
  portalId?: string,
  options?: SasTokenOptions,
): { token: string; failed: boolean } => {
  const alias = useAppSelector((s) => s.alias);
  const globals = useAppSelector((s) => s.globals);
  const appearance = useAppSelector((s) => s.appearance);
  const previewToken = useAppSelector((s) => s.auth.previewSasToken);
  const ws = workspaceId || alias.workspaceId;
  const pid = portalId || alias.portalId || "";
  const isEdge = getIsEdge() === true;
  // Without a portal local_cdn mints a workspace-wide token for a member (an
  // anonymous caller is refused), which opens files from kapps outside the
  // portal. So a portal is required unless the caller opts in; otherwise an
  // alias route whose workspace id resolves before its portal id would cache
  // that token for every consumer. A private portal needs a login before a
  // token can be minted; on edge the session is looked up inside getSassToken.
  const allowWorkspaceScope = !!(options && options.allowWorkspaceScope);
  const canMint =
    !appearance.isPrivate ||
    isEdge ||
    !!(globals.jwtUserIdToken && globals.userId);

  const { data, isError, isPlaceholderData } = useQuery({
    // loaded and isPrivate are in the key so the token is re-minted once the
    // theme says whether the portal is private, and again if that changes;
    // the JWT so a refreshed one re-mints.
    queryKey: queryKeys.sasToken(
      ws,
      pid,
      globals.userId,
      appearance.loaded,
      !!appearance.isPrivate,
      globals.jwtUserIdToken,
    ),
    queryFn: async () => {
      const token = await getSassToken(
        ws,
        pid,
        globals.userId,
        globals.jwtUserIdToken,
        isEdge,
      );
      // getSassToken resolves "" on any failure; throw so React Query retries
      // and re-fetches on the next mount instead of caching the empty token.
      if (!token) throw new Error("sas token mint failed");
      return token;
    },
    enabled: !!ws && (!!pid || allowWorkspaceScope) && canMint && !previewToken,
    // Keep the previous token while a theme-loaded or JWT key change
    // re-mints, so consumers gating on the token don't unmount in the gap. Not
    // across a user change: local_cdn watermarks assets by the token's user,
    // so the next user must never see the previous one's token. Not across a
    // workspace or portal change either: that token is scoped to the old one.
    // Nor when a loaded theme turns private: a public portal's token must not
    // bridge into the private one. The first theme load may flip it; a token
    // minted before then is replaced as soon as the new one lands.
    placeholderData: (prev, prevQuery) =>
      prevQuery &&
      prevQuery.queryKey[1] === ws &&
      prevQuery.queryKey[2] === pid &&
      prevQuery.queryKey[3] === globals.userId &&
      (!prevQuery.queryKey[4] ||
        prevQuery.queryKey[5] === !!appearance.isPrivate)
        ? prev
        : undefined,
    staleTime: SAS_TOKEN_REFRESH,
    refetchInterval: SAS_TOKEN_REFRESH,
    // The token outlives its lifetime (15 min cloud, 30 min edge) in a
    // background tab otherwise; the replaced setInterval mints kept ticking.
    refetchIntervalInBackground: true,
    gcTime: SAS_TOKEN_REFRESH,
  });

  // If the portal turns private, a token cached while it was public must not
  // reach an anonymous visitor while minting is off.
  const token = previewToken || (canMint && data) || "";
  // Same reasons to drop the first token as the query key has, minus the JWT
  // refresh: a token minted before the theme loaded, or while the portal was
  // public, may not open a private portal. A placeholder is the previous
  // key's token, so it is not kept. An edge login or logout switches user
  // without touching globals, so the session generation stands in for it.
  const scope = [
    ws,
    pid,
    globals.userId,
    appearance.loaded,
    !!appearance.isPrivate,
    canMint,
    previewToken,
    userSessions.generation,
  ].join("|");
  const first = useRef({ scope, token: "" });
  if (first.current.scope !== scope) first.current = { scope, token: "" };
  if (!first.current.token && !isPlaceholderData) first.current.token = token;
  return {
    token: options && options.sticky ? first.current.token || token : token,
    failed: !token && isError,
  };
};
